import { IDialogProps } from 'Components/Core/Dialog/types';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { getById, ISieveSize, isLessThan, SieveSizeId } from 'Data/ViewModels/Equipment/ISieveSize';
import { pipe } from 'Lib/Hooks/Selectors';
import { IconRefresh } from 'Lib/Icons';
import { descend, sort } from 'ramda';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actions as psdActions } from '../../../../Data/ReduxViewModels/PsdSieveAnalysis';
import { actions as worksheetEditorActions } from '../../../../Data/ReduxViewModels/WorksheetEditor';
import deepEquals from '../../../../Lib/Object/deepEquals';
import { Button, Dialog, Dropdown, Field, Message, Spinner, TextOutput } from '../../../Core';
import { withInjectedProps } from '../../../Core/InjectedPropsContext';
import { Grid, GridFieldSection } from '../../../Layout';
import { getSeparationSieveSizeIds, getSievingSieveSizeIds, WashingSieveSizeId } from '../Helpers';
import { ManageSeparationsComponentProps } from './types';

const enableSnapshotPrototype = process.env.REACT_APP_ENABLE_SNAPSHOTS_PROTOTYPE === 'true';

let ManageSepartionsDialogContent: React.FC<ManageSeparationsComponentProps> = ({
  createWorksheetSelector,
  maxSeparations,
  defaultSieveSizes,
  notEditableSeparations,
}) => {
  const dispatch = useDispatch();
  const loadingSeparations = useSelector((state) => state.psdSieveAnalysis.separationManagement.status.loading);

  const loadingSeparationsFailed = useSelector(
    (state) => state.psdSieveAnalysis.separationManagement.status.loadingFailed
  );
  const allSieveSizes = useSelector(createWorksheetSelector!('Equipment.AllSieveSizes'), deepEquals);

  const usedSieveSizes = useSelector(pipe(createWorksheetSelector!('', false), getSievingSieveSizeIds), deepEquals);
  const separationSieveSizeIds = useSelector(
    pipe(createWorksheetSelector!('', false), getSeparationSieveSizeIds),
    deepEquals
  );

  // Only show used sieve sizes if at least one sieve aside from separating sieves has been added
  const availableSieveSizes =
    usedSieveSizes.filter((s) => !separationSieveSizeIds.includes(s.Id)).length > 0 ? usedSieveSizes : allSieveSizes;

  const tempSeparations = useSelector((state) => state.psdSieveAnalysis.separationManagement.separations, deepEquals);
  const addSeparationAllowed = maxSeparations ? tempSeparations.length < maxSeparations : true;

  const isNotEditableSeparation = (id: SieveSizeId) => notEditableSeparations?.some((e) => e === id);

  useEffect(() => {
    dispatch(psdActions.initTempSeparations());
  }, [dispatch]);

  const createSeparation = (i: number) => {
    dispatch(psdActions.addTempSeparation({ index: i }));
    // Set default sieve size if valid for this separation and it is not already selected
    // and selecting it will not clear any subsequent separations

    if (
      defaultSieveSizes &&
      defaultSieveSizes.length > i &&
      getSeparationSieveOptions(i).some((o) => defaultSieveSizes[i].includes(o.value)) &&
      !tempSeparations
        .slice(i)
        .some(
          (s: any) =>
            !!s.SeparatingSieveSizeId &&
            (s.SeparatingSieveSizeId === defaultSieveSizes[i] ||
              isSieveSizeSmallerThan(defaultSieveSizes[i], s.SeparatingSieveSizeId))
        )
    ) {
      dispatch(
        psdActions.setTempSeparationValue({ index: i, name: 'SeparatingSieveSizeId', value: defaultSieveSizes[i] })
      );
    }
  };

  if (
    !availableSieveSizes ||
    availableSieveSizes.some((s: ISieveSize) => !s.Size || s.Size.uom !== availableSieveSizes[0].Size.uom)
  )
    throw new Error('Cannot compare sieves sizes. Sizes are missing or are defined with inconsistent units.');

  const isSieveSizeSmallerThan = (sieveSizeIdA: SieveSizeId, sieveSizeIdB: SieveSizeId) =>
    isLessThan(getById(allSieveSizes, sieveSizeIdA), getById(allSieveSizes, sieveSizeIdB));

  const getSieveCaption = (id: SieveSizeId) => getById(allSieveSizes, id)?.Caption;

  const getSeparationSieveOptions = (i: number) => {
    const precedingSeparationSieveSizeId = tempSeparations
      .slice(0, i)
      .reverse()
      .find((s) => s && s.SeparatingSieveSizeId)?.SeparatingSieveSizeId;

    const nextSeparationSieveSizeId = tempSeparations.slice(i + 1).find((s) => s && s.SeparatingSieveSizeId)
      ?.SeparatingSieveSizeId;

    return sort(
      descend((s: ISieveSize) => Number(s.Size?.value)),
      availableSieveSizes.filter(
        (s: ISieveSize) =>
          (!precedingSeparationSieveSizeId || isSieveSizeSmallerThan(s.Id, precedingSeparationSieveSizeId)) &&
          (!nextSeparationSieveSizeId || isSieveSizeSmallerThan(nextSeparationSieveSizeId, s.Id)) &&
          isSieveSizeSmallerThan(WashingSieveSizeId, s.Id)
      )
    ).map((s) => ({
      value: s.Id,
      label: s.Caption,
    }));
  };

  type RowDefinition = {
    data?: any;
    preventRemove?: boolean;
    onRemove?: () => void;
  };
  var gridRows: RowDefinition[] =
    tempSeparations.length > 0
      ? [
          ...(tempSeparations?.map((s: any, i: number) => ({
            data: [
              <Field
                input={<TextOutput />}
                value={i === 0 ? 'Coarse Portion' : `Portion ${i + 1}`}
                label={i === 0 ? 'Coarse Portion' : `Portion ${i + 1}`}
              />,
              <Field
                input={<TextOutput />}
                value={i > 0 ? getSieveCaption((tempSeparations[i - 1] as any).SeparatingSieveSizeId) : 'Coarsest'}
                label={i > 0 ? getSieveCaption((tempSeparations[i - 1] as any).SeparatingSieveSizeId) : 'Coarsest'}
              />,
              isNotEditableSeparation(s.SeparatingSieveSizeId) ? (
                <Field
                  input={<TextOutput />}
                  value={getSeparationSieveOptions(i).find((a) => a.value === s.SeparatingSieveSizeId)?.label}
                />
              ) : (
                <Field
                  input={<Dropdown />}
                  value={s.SeparatingSieveSizeId}
                  onChange={(e: any) =>
                    dispatch(
                      psdActions.setTempSeparationValue({
                        index: i,
                        name: 'SeparatingSieveSizeId',
                        value: e.data.value,
                      })
                    )
                  }
                  options={getSeparationSieveOptions(i)}
                  simpleValue
                />
              ),
              <Button
                disabled={!addSeparationAllowed}
                onClick={() => createSeparation(i)}
                visible={!isNotEditableSeparation(s.SeparatingSieveSizeId)}
              >
                Separate
              </Button>,
            ],
            preventRemove: isNotEditableSeparation(s.SeparatingSieveSizeId),
            onRemove: () => dispatch(psdActions.removeTempSeparation({ index: i })),
          })) ?? []),

          {
            data: [
              <Field input={<TextOutput />} value={'Fine Portion'} label={'Fine Portion'} />,
              <Field
                input={<TextOutput />}
                value={getSieveCaption((tempSeparations[tempSeparations.length - 1] as any).SeparatingSieveSizeId)}
              />,
              <Field input={<TextOutput />} value={'Finest'} label={'Finest'} />,
              <Button disabled={!addSeparationAllowed} onClick={() => createSeparation(tempSeparations.length)}>
                Separate
              </Button>,
            ],
            preventRemove: true,
          },
        ]
      : [
          {
            data: [
              <Field input={<TextOutput />} value={'Sieve Portion'} label={'Sieve Portion'} />,
              <Field input={<TextOutput />} value={'Coarsest'} label={'Coarsest'} />,
              <Field input={<TextOutput />} value={'Finest'} label={'Finest'} />,
              <Button disabled={!addSeparationAllowed} onClick={() => createSeparation(0)}>
                Separate
              </Button>,
            ],
            preventRemove: true,
          },
        ];

  return (
    <>
      <Message>
        Adding or removing separations after data entry has begun may clear data from existing separation or preparation
        sections.
      </Message>
      {loadingSeparations ? (
        <div style={{ position: 'relative', height: `40px`, width: '560px' }}>
          <Spinner />
        </div>
      ) : loadingSeparationsFailed ? (
        <Message severity="error">
          Failed to load separations.
          <Button subtle onClick={() => dispatch(psdActions.initTempSeparations())}>
            <IconRefresh /> Retry
          </Button>
        </Message>
      ) : (
        <GridFieldSection>
          <Grid
            colHeaders={['Sieve Portion', 'Material From', 'Material To']}
            colWidths={[110, 120, 160, 80]}
            data={gridRows.map((r) => r.data)}
            onRemoveRow={(i) => gridRows[i].onRemove?.()}
            rows={gridRows.map((r) => ({ preventRemoveRow: r.preventRemove }))}
          />
        </GridFieldSection>
      )}
    </>
  );
};
ManageSepartionsDialogContent = withInjectedProps(ManageSepartionsDialogContent);

type ManageSepartionsDialogProps = IWorksheetComponent & IDialogProps & ManageSeparationsComponentProps;
let ManageSeparationsDialog: React.FC<ManageSepartionsDialogProps> = ({
  onClose,
  visible,
  namePrefix,
  maxSeparations,
  defaultSieveSizes,
  notEditableSeparations,
  ...otherProps
}) => {
  const dispatch = useDispatch();

  const [closeWhenSubmitted, setCloseWhenSubmitted] = useState(false);
  const submittingSeparations = useSelector((state) => state.psdSieveAnalysis.separationManagement.status.submitting);
  const separationSubmissionFailed = useSelector(
    (state) => state.psdSieveAnalysis.separationManagement.status.submissionFailed
  );

  const canConfirm = useSelector(
    (state) => !state.psdSieveAnalysis.separationManagement.separations.some((s) => !s.SeparatingSieveSizeId)
  );

  useEffect(() => {
    if (!submittingSeparations && !separationSubmissionFailed && closeWhenSubmitted) {
      setCloseWhenSubmitted(false);
      if (visible) onClose?.();
    }
  }, [visible, submittingSeparations, closeWhenSubmitted, separationSubmissionFailed, onClose]);

  const applyChanges = () => {
    if (enableSnapshotPrototype) {
      dispatch(
        worksheetEditorActions.addSnapshot({
          description: 'Auto: Before separations change',
        })
      );
    }
    dispatch(psdActions.applyTempSeparationsToWorksheet());
    setCloseWhenSubmitted(true);
  };

  return (
    <Dialog
      {...otherProps}
      title="Manage Separations"
      width="560px"
      minHeight={`${200 + 40 * (maxSeparations! + 1)}px`}
      visible={visible}
      onClose={submittingSeparations ? undefined : onClose}
      buttons={[
        <Button
          primary
          key="1"
          onClick={applyChanges}
          disabled={!canConfirm || submittingSeparations}
          loading={submittingSeparations}
        >
          Done
        </Button>,
        <Button key="0" disabled={submittingSeparations} onClick={onClose as any}>
          Cancel
        </Button>,
      ]}
    >
      {visible && (
        <ManageSepartionsDialogContent
          maxSeparations={maxSeparations}
          defaultSieveSizes={defaultSieveSizes}
          notEditableSeparations={notEditableSeparations}
        />
      )}
    </Dialog>
  );
};

ManageSeparationsDialog = withInjectedProps(ManageSeparationsDialog);

export { ManageSeparationsDialog };
